import React from "react";
import { motion } from "framer-motion";
import {
  Lightning, Sun, BatteryCharging, Plug, Cpu, Palette, SquaresFour, Bed, Drop, Truck, Tire, LinkSimpleHorizontal,
} from "@phosphor-icons/react";
import { OptionsList } from "@/components/customer/OptionsList";
import { HubField } from "@/components/customer/caravan/HubField";

export const COLUMN_GROUPS = [
  {
    id: "power_electrical",
    title: "Power & Electrical",
    icon: Lightning,
    fields: [
      { key: "battery_configuration", label: "Battery", icon: BatteryCharging, type: "pill" },
      { key: "inverter_charger", label: "Inverter / Charger", icon: Plug, type: "pill" },
      { key: "solar_configuration", label: "Solar Configuration", icon: Sun, type: "callout" },
      { key: "management_system", label: "Management System", icon: Cpu, type: "callout" },
    ],
  },
  {
    id: "appearance_styling",
    title: "Appearance & Styling",
    icon: Palette,
    fields: [
      { key: "interior_palette", label: "Interior Palette", type: "swatch", palette: true },
      { key: "exterior_colour", label: "Exterior Colour", type: "swatch" },
      { key: "decal_colour", label: "Decal Colour", type: "swatch" },
    ],
  },
  {
    id: "chassis",
    title: "Chassis",
    icon: Tire,
    fields: [
      { key: "axle", label: "Axle", icon: Truck, type: "pill" },
      { key: "hitch", label: "Hitch", icon: LinkSimpleHorizontal, type: "pill" },
      { key: "hub", label: "Hubs", type: "hub" },
    ],
  },
  {
    id: "plumbing",
    title: "Plumbing",
    icon: Drop,
    fields: [{ key: "water_tank_capacity", label: "Plumbing Configuration", icon: Drop, type: "callout" }],
  },
];

export const OPTIONS_GROUP = {
  id: "layout_options",
  title: "Options & Extras",
  icon: SquaresFour,
  fields: [{ key: "additional_options", label: "Additional Options", icon: Bed, type: "list" }],
};

const SWATCH = {
  BROWN: "#6e473b", GREY: "#6b7280", GRAY: "#6b7280", BLACK: "#18181b", WHITE: "#f4f4f5",
  SILVER: "#9ca3af", CHARCOAL: "#3f3f46", BEIGE: "#d4b996", SAND: "#d4b996",
  BLUE: "#3b82f6", RED: "#dc2626", GREEN: "#16a34a", ORANGE: "#f97316", GOLD: "#d4af37",
};

function colorFor(value) {
  const u = (value || "").toUpperCase();
  for (const k of Object.keys(SWATCH)) if (u.includes(k)) return SWATCH[k];
  return "#525252";
}

function parseTechnical(v) {
  if (!v) return [];
  let parts = v.split(";").map((s) => s.trim()).filter(Boolean);
  if (parts.length < 2) parts = v.split(/,\s*/).map((s) => s.trim()).filter(Boolean);
  return parts;
}

export const LABEL_CLS = "font-body text-[0.7rem] font-medium uppercase tracking-[0.12em] text-neutral-400";
const CARD_CLS = "bg-[#141414] border border-neutral-800 hover:border-neutral-700 rounded-xl p-4 transition-colors duration-200";

function FieldLabel({ field }) {
  const Icon = field.icon;
  return (
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon size={15} className="text-[#73bf44]" weight="duotone" />}
      <span className={LABEL_CLS}>{field.label}</span>
    </div>
  );
}

function PillCard({ field, value }) {
  return (
    <div data-testid={`spec-field-${field.key}`} className={CARD_CLS}>
      <FieldLabel field={field} />
      <div className="font-body text-sm font-semibold text-white leading-snug">{value}</div>
    </div>
  );
}

function SwatchField({ field, value }) {
  const color = field.palette ? "#73bf44" : colorFor(value);
  return (
    <div data-testid={`spec-field-${field.key}`} className={CARD_CLS}>
      <span className={LABEL_CLS}>{field.label}</span>
      <div className="flex items-center gap-2.5 mt-2">
        <span className="w-4 h-4 rounded-full border border-white/20 shadow-sm shrink-0" style={{ background: color }} />
        <span className="font-body text-sm font-semibold text-white uppercase tracking-wide">{value}</span>
      </div>
    </div>
  );
}

function TechnicalCallout({ field, value }) {
  const segments = parseTechnical(value);
  return (
    <div
      data-testid={`spec-field-${field.key}`}
      className="relative bg-[#111111] border border-neutral-800 rounded-xl p-5 pl-6 overflow-hidden hover:border-[#73bf44]/40 transition-colors duration-200"
    >
      <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#73bf44]" />
      <FieldLabel field={field} />
      {segments.length > 1 ? (
        <ul className="space-y-2 mt-1">
          {segments.map((s, i) => (
            <li key={i} className="flex gap-2.5 font-body text-sm text-neutral-200 leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#73bf44] shrink-0" />
              <span>{s}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="font-body text-sm text-neutral-200 leading-relaxed">{value}</p>
      )}
    </div>
  );
}

function FullField({ field, caravan, onCaravanChange }) {
  if (field.type === "hub") return <HubField caravan={caravan} onChange={onCaravanChange} />;
  if (field.type === "list") return <OptionsList field={field} value={caravan[field.key]} />;
  return <TechnicalCallout field={field} value={caravan[field.key]} />;
}

export function GroupHeading({ icon: Icon, title, aside }) {
  return (
    <div className="flex items-center gap-2.5 mb-5">
      <div className="w-8 h-8 rounded-lg bg-[#73bf44]/10 flex items-center justify-center">
        <Icon size={18} className="text-[#73bf44]" weight="duotone" />
      </div>
      <h3 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-[#73bf44]">{title}</h3>
      {aside && <div className="ml-auto">{aside}</div>}
    </div>
  );
}

export function SpecGroup({ group, caravan, index = 0, onCaravanChange }) {
  const present = group.fields.filter((f) => f.type === "hub" || caravan[f.key]);
  if (present.length === 0) return null;
  const compact = present.filter((f) => ["pill", "swatch"].includes(f.type));
  const full = present.filter((f) => !["pill", "swatch"].includes(f.type));
  return (
    <motion.section
      data-testid={`spec-group-${group.id}`}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.04 + index * 0.06, ease: "easeOut" }}
      className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-5 sm:p-6"
    >
      <GroupHeading icon={group.icon} title={group.title} />
      {compact.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 last:mb-0">
          {compact.map((f) =>
            f.type === "swatch" ? <SwatchField key={f.key} field={f} value={caravan[f.key]} /> : <PillCard key={f.key} field={f} value={caravan[f.key]} />
          )}
        </div>
      )}
      {full.length > 0 && (
        <div className="space-y-3">
          {full.map((f) => <FullField key={f.key} field={f} caravan={caravan} onCaravanChange={onCaravanChange} />)}
        </div>
      )}
    </motion.section>
  );
}

export function ExtraDetails({ caravan }) {
  const entries = Object.entries(caravan.specifications || {}).filter(([, v]) => v);
  if (entries.length === 0) return null;
  return (
    <section data-testid="spec-group-additional" className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-5 sm:p-6">
      <GroupHeading icon={SquaresFour} title="Additional Details" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {entries.map(([k, v]) => (
          <div key={k} className="bg-[#141414] border border-neutral-800 rounded-xl p-4">
            <span className={LABEL_CLS}>{k}</span>
            <div className="font-body text-sm font-semibold text-white leading-snug mt-2">{String(v)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
